// Revenue chart section — bar chart of daily revenue with period toggle.
// Extracted from Reports.tsx during Maintainability Sprint 2026-04-19
// (Task 1.2 step 2). See docs/superpowers/plans/.
import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { BarChart3 } from "lucide-react";
import type { RevenuePeriod, RevenueResponse } from "@/lib/admin-api/finance";
import { formatFcfa, formatShortDate } from "./formatters";
import ChartTooltip from "./ChartTooltip";

interface RevenueChartSectionProps {
  readonly period: RevenuePeriod;
  readonly onPeriodChange: (period: RevenuePeriod) => void;
  readonly data: RevenueResponse | undefined;
  readonly isLoading: boolean;
}

export default function RevenueChartSection({
  period,
  onPeriodChange,
  data,
  isLoading,
}: RevenueChartSectionProps) {
  const series = data?.series ?? [];

  // Prepare chart data with short date labels
  const chartData = useMemo(
    () =>
      series.map((point) => ({
        ...point,
        label: formatShortDate(point.date),
      })),
    [series],
  );

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-stone-900">
            Revenu par jour
          </h3>
          <p className="text-xs text-stone-500">
            {data?.kpis
              ? `Total: ${formatFcfa(data.kpis.total_fcfa)} — ${data.kpis.orders_count} commande(s)`
              : "Chargement..."}
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-stone-200 bg-stone-50 p-1">
          {(
            [
              { id: "day", label: "Jour" },
              { id: "week", label: "Semaine" },
              { id: "month", label: "Mois" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPeriodChange(opt.id)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                period === opt.id
                  ? "bg-white text-stone-900 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-center">
          <BarChart3 className="h-8 w-8 text-stone-300" />
          <p className="mt-2 text-sm text-stone-500">Aucune donnee pour cette periode</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#78716c" }}
              tickLine={false}
              axisLine={{ stroke: "#d6d3d1" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#78716c" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend
              formatter={(value: string) =>
                value === "revenue_fcfa" ? "Revenu (FCFA)" : "Commandes"
              }
              wrapperStyle={{ fontSize: 12 }}
            />
            <Bar
              dataKey="revenue_fcfa"
              fill="#C75B39"
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
